import { untracked, useComputed } from "@suse-o11y/signal";
import React from "react";
import { useSelector } from "react-redux";

import { pluralize } from "~/tools/pluralize";
import { Template } from "~/tools/template";

import { selectTimestampDate } from "../../../../../app/state/view/timeline/TimelineSelectors";
import { DashboardChartPluginSpec, DashboardWidgetSpec } from "../../../../../common/dashboard-spec";
import { TimeSeriesQuery } from "../../../../../common/dashboard-spec/_time-series-query";
import { useExplorationDrawerState } from "../../../../../components/react/drawer/use-exploration-drawer-state";
import { useTimelineInterval } from "../../../../../components/react/timeline/useTimelineInterval";
import { fromMetricQuery as widgetQueryFromMetricQuery } from "../../../../../components/widget-form/query/spec";
import { generatePath } from "../../../../../generic/routing";
import { COMPONENT_PATH } from "../../../../../routes/Routes";
import { EastPaneLayout } from "../EastPaneLayout";
import { EastPaneNotFound } from "../EastPaneNotFound";

import { Metric } from "./_metric";
import { MonitorStatus } from "./_monitor-status";
import { usePinnedItemsState } from "./_pinned-items-context";
import { PinnedItemsSection } from "./_pinned-items-section";

const pluralizeMonitorStatus = pluralize({
	1: "Monitor status",
	more: "Monitor statuses",
});

const pluralizeMetric = pluralize({
	1: "Metric",
	more: "Metrics",
});

export const PinnedItems: React.FC = () => {
	const explorationDrawer = useExplorationDrawerState();
	const timestamp = useSelector(selectTimestampDate);
	const timeRange = useTimelineInterval();
	const state = usePinnedItemsState();
	const isPinnedItemsEmpty = useComputed((monitor) => state.size(monitor) === 0, [state]);

	if (isPinnedItemsEmpty) {
		return <EastPaneNotFound type="PinnedItems" />;
	}

	return (
		<EastPaneLayout>
			<div>
				<PinnedItemsSection
					getCardComponentReference={({ component, topologyTime }) => ({
						...component,
						timestamp: topologyTime,
					})}
					getCardDescription={({ monitorDescription }) => monitorDescription}
					getCardTitle={({ message }) => message}
					name={pluralizeMonitorStatus}
					onEnlarge={({ timeRange: pinnedTimeRange, ...monitorStatus }) => {
						explorationDrawer.openMonitorStatus({
							identifier: monitorStatus.identifier,
							timestamp: monitorStatus.topologyTime,
							timeRange: untracked(pinnedTimeRange),
						});
					}}
					state={state.monitorStatuses}
				>
					{({ timeRange: pinnedTimeRange, ...monitorStatus }) => (
						<MonitorStatus monitorStatus={monitorStatus} timeRange={pinnedTimeRange} />
					)}
				</PinnedItemsSection>

				<PinnedItemsSection
					getCardComponentReference={({ component }) => component}
					getCardDescription={({ description }) => description}
					getCardTitle={({ name }) => name}
					name={pluralizeMetric}
					onAddToDashboard={(items) => {
						explorationDrawer.openAddToDashboard(
							items.map((item) =>
								DashboardWidgetSpec.of({
									name: Template(item.name ?? ""),
									plugin: {
										queries: item.metricQueries.map((query) =>
											TimeSeriesQuery.fromQuerySpec(widgetQueryFromMetricQuery(query)),
										),
										links: [
											{
												url: Template(`/#${generatePath(COMPONENT_PATH, { identifier: item.component.identifier })}`),
												name: Template(`${item.component.name} (${item.component.type})`),
												targetBlank: false,
											},
										],
										...DashboardChartPluginSpec.fromChartSpec(item.chart),
									},
								}),
							),
						);
					}}
					onEnlarge={(metric) => {
						explorationDrawer.openMetrics({
							title: metric.name,
							chart: metric.chart,
							metricQueries: metric.metricQueries,
							componentIdOrIdentifier: metric.component.identifier,
							timeRange: timeRange.clone(),
							timestamp,
						});
					}}
					state={state.metrics}
				>
					{(metric) => <Metric markerTimestamp={timestamp} metric={metric} timeRange={timeRange} />}
				</PinnedItemsSection>
			</div>
		</EastPaneLayout>
	);
};
